if (!customElements.get("bundle-sale-form")) {
  customElements.define(
    "bundle-sale-form",
    class BundleSaleForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        if (!this.form) return;

        this.isloading = false; // Loading state

        // Cart drawer reference
        this.cartDrawer = document.getElementById("Cart-Drawer");

        // Add to cart button
        this.addCartButton = this.querySelector("button[type='submit']");

        this.boundOnSubmitHandler = this.onSubmitHandler.bind(this);
        this.boundOnChange = this.onChange.bind(this);
        this.form.addEventListener("submit", this.boundOnSubmitHandler); // Add-to-cart listener
        this.form.addEventListener("change", this.boundOnChange); // Product quantity change listener

        // THMV F1-08/F6-12: clique no card inteiro seleciona o item, ver
        // thmv-overrides.liquid
        this.boundOnCardClick = this.onCardClick.bind(this);
        this.form.addEventListener("click", this.boundOnCardClick);

        // THMV F6-12: total combinado (produto principal + itens
        // marcados) recalcula quando a variante do produto principal
        // muda (o preço pode mudar sem disparar "change" neste form).
        // Ver thmv-overrides.liquid.
        this.boundOnVariantChange = this.onVariantChange.bind(this);
        this.variantChangeUnsubscriber = webvista.subscribe(
          PUB_SUB_EVENTS.variantChange,
          this.boundOnVariantChange,
        );

        // THMV F6-31: total combinado também recalcula quando a
        // quantidade do produto principal muda. O input de quantidade
        // principal (buy-buttons.liquid) fica FORA deste form (é outro
        // <form> na PDP), então o listener de "change" no form do bundle
        // (this.form, acima) nunca captura essa mudança — só pegava
        // indiretamente via data-allow-external-quantity-update
        // (quantity_sync), que foi desligado no F6-12/F6-13 pra não
        // sobrescrever a quantidade de cada item. Esse listener dedicado
        // recalcula o total sem tocar na quantidade dos itens. Ver
        // thmv-overrides.liquid.
        this.mainQuantityInput = document.getElementById(
          `Quantity-${this.dataset.section}`,
        );
        if (this.mainQuantityInput) {
          this.boundOnMainQuantityChange = () => this.summaryTotal();
          this.mainQuantityInput.addEventListener(
            "change",
            this.boundOnMainQuantityChange,
          );
        }

        // Correct quantity selector boundaries and values
        this.setQuantityBoundaries();

        // Validate selection state
        this.validateSelection();

        // Initialize total price calculation
        this.summaryTotal();

        // Initialize quantity sync listeners
        if (!this.hasAttribute("data-show-quantity-selector"))
          this.bindQuantitySync();

        // Listen for cart update events and update quantity selectors
        // This handles updates from other locations, such as the cart or product page forms
        this.cartUpdateUnsubscriber = webvista.subscribe(
          PUB_SUB_EVENTS.cartUpdate,
          (event) => {
            if (event.source === "bundle-sale-form") return;

            // Check if the variant is one of the currently listed variants
            const variantIds = Array.from(
              this.querySelectorAll("input[name='id']"),
            ).map((input) => String(input.value)); // Use String to ensure type consistency
            if (!variantIds.includes(String(event.productVariantId))) return;

            // Update quantity selectors
            if (this.fetchController) this.fetchController.abort();
            this.fetchController = new AbortController();

            webvista
              .fetchHtml(
                `${window["routes"]["root_url"]}?section_id=${this.dataset.section}`,
                this.fetchController.signal,
              )
              .then((html) => {
                this.updateBundleQuantityRulers(html);
                this.setQuantityBoundaries();
                // Recalculate total since quantity selector values may have been reset
                this.summaryTotal();
              })
              .catch((error) => {
                // console.log(error);
              });
          },
        );
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }

        if (this.form && this.boundOnSubmitHandler) {
          this.form.removeEventListener("submit", this.boundOnSubmitHandler);
        }

        if (this.form && this.boundOnChange) {
          this.form.removeEventListener("change", this.boundOnChange);
        }

        if (this.form && this.boundOnCardClick) {
          this.form.removeEventListener("click", this.boundOnCardClick);
        }

        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }

        if (this.mainQuantityInput && this.boundOnMainQuantityChange) {
          this.mainQuantityInput.removeEventListener(
            "change",
            this.boundOnMainQuantityChange,
          );
        }

        if (this.fetchController) {
          this.fetchController.abort();
        }
      }

      /**
       * Handles change events and updates total price.
       */
      onChange(event) {
        // THMV F1-08/F6-12: ao marcar um item, traz a quantidade atual do
        // produto principal como valor inicial — o cliente ajusta depois
        // se quiser, sem que mudanças futuras na quantidade principal
        // sobrescrevam o que ele já escolheu. Ver thmv-overrides.liquid.
        if (event.target.matches("input[name='id']") && event.target.checked) {
          this.applyMainQuantity(event.target);
        }

        this.validateSelection();

        // Update total price
        setTimeout(() => {
          this.summaryTotal();
        });
      }

      /**
       * THMV F1-08/F6-12: copia a quantidade atual do produto principal da
       * PDP para o item recém-marcado, respeitando o min/max daquele item.
       * Ver thmv-overrides.liquid.
       * @param {HTMLInputElement} checkbox - Checkbox do item marcado
       */
      applyMainQuantity(checkbox) {
        const mainQuantityInput = document.getElementById(
          `Quantity-${this.dataset.section}`,
        );
        if (!mainQuantityInput) return;

        const quantityInput = checkbox
          .closest(".bundle-product-card")
          ?.querySelector("input[name='quantity']");
        if (!quantityInput) return;

        const min = quantityInput.min ? parseInt(quantityInput.min) : 1;
        const max = quantityInput.max ? parseInt(quantityInput.max) : null;
        let quantity = parseInt(mainQuantityInput.value) || min;
        if (max !== null) quantity = Math.min(quantity, max);
        quantity = Math.max(quantity, min);

        quantityInput.value = quantity;
        quantityInput.dispatchEvent(new Event("change", { bubbles: true }));
      }

      /**
       * Validates product selection state and updates submit button state.
       */
      validateSelection() {
        this.noValidSelection = !this.querySelector(
          "input[name='id']:checked:not(:disabled)",
        );
        this.setButtonState(this.noValidSelection);
      }

      /**
       * THMV F6-12: recalcula o total quando a variante do produto
       * principal muda (o preço pode mudar sem disparar "change" neste
       * form). Ver thmv-overrides.liquid.
       * @param {{data: {sectionId: string}}} event
       */
      onVariantChange(event) {
        if (event.data.sectionId !== this.dataset.section) return;
        this.summaryTotal();
      }

      /**
       * THMV F6-12: lê o preço atual do produto principal, exposto por
       * thmv-price.liquid (data-thmv-price-value, em centavos). Ver
       * thmv-overrides.liquid.
       * @returns {number}
       */
      getMainProductPrice() {
        const priceEl = document.querySelector(
          `#Price-${this.dataset.section} [data-thmv-price-value]`,
        );
        return parseFloat(priceEl?.dataset.thmvPriceValue) || 0;
      }

      /**
       * Calculates and displays the bundle total price.
       *
       * THMV F1-08/F6-12: os componentes marcados aqui são adicionados ao
       * carrinho junto com o produto principal no mesmo clique (não há
       * botão de adicionar separado neste bloco) — por isso o total
       * combina o preço do produto principal (quantidade atual) com o
       * dos itens marcados, e não só os itens. Ver thmv-overrides.liquid.
       */
      summaryTotal() {
        const mainQuantityInput = document.getElementById(
          `Quantity-${this.dataset.section}`,
        );
        const mainQuantity = parseInt(mainQuantityInput?.value) || 1;
        const mainTotal = this.getMainProductPrice() * mainQuantity;

        const bundleProductList = Array.from(
          this.querySelectorAll(".bundle-product-card"),
        );

        const total = bundleProductList.reduce(
          (sum, item) => {
            if (!item.querySelector('input[name="id"]')?.checked) return sum;

            const price =
              parseFloat(
                item.querySelector(".card-price .price")?.dataset.value,
              ) || 0;

            const originalPrice =
              parseFloat(
                item.querySelector(".card-price .price")?.dataset.original,
              ) || price; // Fall back to current price when original is absent

            const quantity =
              parseInt(item.querySelector("input[name='quantity']")?.value) ||
              1;

            return {
              current: sum.current + price * quantity,
              original: sum.original + originalPrice * quantity,
            };
          },
          { current: mainTotal, original: mainTotal },
        );

        const priceEl = this.querySelector(".bundle-total .price");
        if (priceEl) {
          priceEl.textContent = webvista.formatPriceAmount(
            total.current,
            Shopify.currency.active,
          );
        }

        const originalEl = this.querySelector(".bundle-total .original");
        if (originalEl) {
          if (total.original > total.current) {
            // Only show original price when it exceeds the current price
            originalEl.hidden = false;
            originalEl.textContent = webvista.formatPriceAmount(
              total.original,
              Shopify.currency.active,
            );
          } else {
            // Otherwise hide it
            originalEl.hidden = true;
            originalEl.textContent = "";
          }
        }

        this.updatePixTotal(total.current);
      }

      /**
       * THMV F6-12: mostra o equivalente no PIX do total combinado,
       * usando o percentual global já configurado na loja (mesma conta
       * de settings.thmv_pix_discount_percentage). Ver
       * thmv-overrides.liquid.
       * @param {number} totalCurrent - Total combinado atual, em centavos
       */
      updatePixTotal(totalCurrent) {
        if (!this.hasAttribute("data-pix-enabled")) return;

        const pixValueEl = this.querySelector(".bundle-total-pix-value");
        if (!pixValueEl) return;

        const discount = parseFloat(this.dataset.pixDiscount) || 0;
        const pixTotal = Math.round((totalCurrent * (100 - discount)) / 100);

        pixValueEl.textContent = webvista.formatPriceAmount(
          pixTotal,
          Shopify.currency.active,
        );
      }

      /**
       * Handles the add-to-cart form submission.
       */
      onSubmitHandler(event) {
        if (this.isloading) return;
        event.preventDefault();

        // Submit to cart
        const btn = event.submitter || this.addCartButton;
        this.addToCart(btn);
      }

      /**
       * Executes the submission to the cart
       * @param button Submit button
       */
      addToCart(button = null) {
        if (this.noValidSelection) return;

        this.startLoading();

        const config = webvista.fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";

        // All valid checked variant IDs
        const items = this.getSelectedItems();
        if (items.length <= 0) {
          this.setButtonState(); // Disable submit button
          this.endLoading();
          return;
        }

        config.body = JSON.stringify({
          items: items,
          sections: this.getSectionsToRender().map(
            (section) => section.section,
          ),
        });

        fetch(`${window["routes"]["cart_add_url"]}`, config)
          .then((response) => {
            return response.json();
          })
          .then((response) => {
            if (response.status) {
              webvista.popToast(response.description, "error");
              return (this.error = true);
            }

            this.error = false;

            // Update bundle product quantity rules
            const html = new DOMParser().parseFromString(
              response["sections"][this.dataset.section],
              "text/html",
            );
            this.updateBundleQuantityRulers(html);
            // Correct quantity input boundaries
            this.setQuantityBoundaries();
            // Recalculate total since quantity selector values may have been reset
            this.summaryTotal();

            // Filter out the current section — no need to replace its HTML
            const sectionsNeedRender = this.getSectionsToRender().filter(
              (section) => section.id !== this.id,
            );

            // Dynamically update section contents
            SectionDynamicUpdate.updateSections(
              sectionsNeedRender,
              response["sections"],
            );

            // Publish cart update events
            // Each variant change needs its own event
            items.forEach((item) => {
              webvista.publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "bundle-sale-form",
                productVariantId: item["id"],
              });
            });

            if (
              this.cartDrawer &&
              !this.cartDrawer.hasAttribute("data-status-silence")
            ) {
              // Open the cart drawer
              this.cartDrawer.show(button);
            } else {
              return (window.location = window["routes"]["cart_url"]);
            }
          })
          .catch((error) => {
            // console.log(error);
            webvista.popToast(
              window["accessibilityStrings"]["unknownError"],
              "error",
            );
            this.error = true;
          })
          .finally(() => {
            this.endLoading();
          });
      }

      /**
       * Get selected items
       * @param {string} parentId Parent ID (optional)
       * @returns {Array} Returns an array of selected items, each containing ID and quantity
       */
      getSelectedItems(parentId = null) {
        return Array.from(
          this.querySelectorAll("input[name='id']:checked:not(:disabled)"),
        ).map((input) => {
          const quantityInput = input
            .closest(".bundle-product-card")
            ?.querySelector("input[name='quantity']");
          return {
            id: input.value,
            quantity: parseInt(quantityInput?.value) || 1,
            ...(parentId && { parent_id: parentId }),
          };
        });
      }

      /**
       * Binds quantity input synchronization with display elements.
       */
      bindQuantitySync() {
        this.querySelectorAll("input[name='quantity']").forEach((input) => {
          input.addEventListener("change", (event) => {
            const value = event.target.value || 1;

            const displayElement = this.querySelector(
              `.quantity-count[data-product="${event.target.dataset.product}"]`,
            );
            if (displayElement) displayElement.textContent = value;
          });
        });
      }

      /**
       * Updates the quantity of items via external call
       * @param {number} value - The quantity to set
       */
      setItemsQuantity(value) {
        // Check if external quantity updates are allowed
        if (!this.hasAttribute("data-allow-external-quantity-update")) return;

        const quantity = value ?? 1;
        this.querySelectorAll("input[name='quantity']").forEach((input) => {
          input.value = quantity;

          // Manually trigger the change event
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      /**
       * THMV F1-08/F6-12: expande a área de clique de seleção do item para
       * o card inteiro (imagem, título, preço), fora dos elementos
       * interativos que já têm comportamento próprio (link do título,
       * seletor de variante, botões de quantidade, o próprio
       * checkbox/label). Ver thmv-overrides.liquid.
       */
      onCardClick(event) {
        if (event.target.closest("a, button, input, label, modal-opener, quantity-input"))
          return;

        const card = event.target.closest(".bundle-product-card");
        if (!card) return;

        const checkbox = card.querySelector("input[name='id']");
        if (!checkbox || checkbox.disabled) return;

        checkbox.checked = !checkbox.checked;
        checkbox.dispatchEvent(new Event("change", { bubbles: true }));
      }

      /**
       * Returns the sections that need to be re-rendered after cart update.
       * @returns {[{section: string, id: string},{section: string, selector: string, id: string}]}
       */
      getSectionsToRender() {
        const sections = [
          {
            id: this.id,
            section: this.dataset.section,
          },
          {
            id: "Cart-Icon-Bubble",
            section: "cart-icon-bubble",
            selector: ".shopify-section",
          },
          {
            id: "Bottom-Cart-Bubble",
            section: "cart-icon-bubble",
            selector: ".shopify-section",
          },
          {
            id: "Cart-Drawer",
            section: "cart-drawer",
            selector: ".cart-count",
          },
        ];

        if (
          this.cartDrawer &&
          !this.cartDrawer.hasAttribute("data-status-silence")
        ) {
          sections.push({
            id: "Cart-Drawer",
            section: this.cartDrawer.dataset.section,
            selector: "#Cart-Drawer-Details",
          });
        }

        return sections;
      }

      /**
       * Updates bundle product quantity rules from fetched HTML.
       * @param html Parsed HTML document containing updated quantity data
       */
      updateBundleQuantityRulers(html) {
        const targetQuantityInputs = this.querySelectorAll(
          "input[name='quantity']",
        );
        targetQuantityInputs.forEach((targetInput) => {
          const sourceInput = html.getElementById(targetInput.id);

          if (sourceInput) {
            const attributes = [
              "data-cart-quantity",
              "data-min",
              "data-max",
              "step",
            ];

            for (let attribute of attributes) {
              const sourceInputValue = sourceInput.getAttribute(attribute);
              if (sourceInputValue != null) {
                targetInput.setAttribute(attribute, sourceInputValue);
              }
            }
          }
        });
      }

      /**
       * Corrects quantity input boundaries for all quantity selectors.
       */
      setQuantityBoundaries() {
        this.querySelectorAll("quantity-input").forEach((quantityInput) => {
          quantityInput.setQuantityBoundaries();
        });
      }

      startLoading() {
        this.isloading = true;

        if (this.addCartButton) {
          this.addCartButton.setAttribute("disabled", "disabled");
          this.addCartButton.setAttribute("aria-busy", "true");
          this.addCartButton.classList.add("loading");
        }
      }

      endLoading() {
        this.isloading = false;

        if (this.addCartButton) {
          this.addCartButton.removeAttribute("disabled");
          this.addCartButton.setAttribute("aria-busy", "false");
          this.addCartButton.classList.remove("loading");
        }
      }

      /**
       * Sets the submit button enabled/disabled state.
       * @param disabled Whether to disable the button
       */
      setButtonState(disabled = true) {
        this.addCartButton?.toggleAttribute("disabled", disabled);
      }
    },
  );
}
